import { FC, useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { AnimatePresence, motion } from "framer-motion"
import { Icon } from "./icons/Icon"
import { Button } from "./ui/Button"
import { Spinner } from "./ui/Spinner"
import { PSApiOperations } from "@/api/PSApiOperations"
import { HelpArticle } from "@/api/dto/help-center"
import { useIntercom } from "@/hooks/useIntercom"
import { pixiesetURLs } from "@/utils/pixieset-urls"

const HelpItemComponent: FC<{ item: HelpArticle }> = ({ item }) => {
	return (
		<li className="hover:bg-base-700">
			<a
				href={item.url}
				target="_blank"
				rel="noopener noreferrer"
				className="block px-5 py-4"
			>
				<div className="flex flex-col gap-1.5">
					<span className="text-md font-semibold text-base-50">{item.name}</span>
					<span className="line-clamp-2 text-sm text-base-400">{item.description}</span>
				</div>
			</a>
		</li>
	)
}

export const HelpCenter: FC = () => {
	const { t } = useTranslation("components")
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [helpItems, setHelpItems] = useState<HelpArticle[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const { initialize, openIntercom, isInitialized, isLoading: intercomLoading } = useIntercom()

	const dropdownRef = useRef<HTMLDivElement>(null)
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const abortControllerRef = useRef<AbortController | null>(null)

	const fetchHelpArticles = async (search: string, signal: AbortSignal) => {
		setIsLoading(true)
		setError(null)
		setHelpItems([])

		try {
			const route = window.location.pathname.replace(/\/\d+/g, "/*").replace(/\/$/, "") || "/"
			const result = await PSApiOperations.searchHelpArticles(
				{
					route,
					application: "photo-editor",
					search,
					results: 3,
				},
				signal,
			)

			if (!signal.aborted) {
				setHelpItems(
					result.data.map((dto) => ({
						id: dto.id,
						url: dto.url,
						name: dto.name,
						description: dto.body,
					})),
				)
			}
		} catch (err) {
			if (!signal.aborted) {
				setError(t("helpCenter.failedToLoad"))
				setHelpItems([])
			}
		} finally {
			if (!signal.aborted) {
				setIsLoading(false)
			}
		}
	}

	const handleContactClick = () => {
		setIsOpen(false)
		void openIntercom()
	}

	const handleClearSearch = () => {
		setSearchQuery("")
	}

	useEffect(() => {
		if (isOpen && !isInitialized && !intercomLoading) {
			void initialize()
		}
	}, [isOpen, isInitialized, intercomLoading, initialize])

	// Search with debounce
	useEffect(() => {
		if (!isOpen) {
			return
		}

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}

		setIsLoading(true)

		searchTimeoutRef.current = setTimeout(() => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
			abortControllerRef.current = new AbortController()
			void fetchHelpArticles(searchQuery, abortControllerRef.current.signal)
		}, 300)

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
		}
	}, [searchQuery, isOpen])

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside)
			return () => document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [isOpen])

	// Reset search when closing dropdown
	useEffect(() => {
		if (!isOpen) {
			setSearchQuery("")
			setHelpItems([])
			setError(null)
		}
	}, [isOpen])

	const hasResults = helpItems.length > 0
	const hasSearchQuery = searchQuery.trim().length > 0
	const showSeeMore = hasSearchQuery && hasResults

	const helpCenterUrl = showSeeMore
		? pixiesetURLs.help.hcEnUs
		: pixiesetURLs.help.hcEnUsSearch(searchQuery)

	const linkText = showSeeMore ? t("helpCenter.seeMoreResults") : t("helpCenter.browseHelpCenter")

	return (
		<div className="relative" ref={dropdownRef}>
			<Button
				variant="custom"
				size="custom"
				trackingLabel="open_help_center"
				aria-label={t("helpCenter.ariaOpenHelpCenter")}
				className="flex size-10 items-center justify-center rounded-md text-base-400 hover:text-base-200"
				onClick={() => setIsOpen(!isOpen)}
			>
				<Icon name="help" size={20} />
			</Button>

			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="fixed left-8 top-[70px] z-50 w-[340px] rounded-md border border-base-700 bg-base-800 font-proxima shadow-lg"
					>
						<div className="flex items-center gap-3 border-b border-base-700 px-4 py-3">
							<Icon name="search" size={24} className="shrink-0 text-base-400" />
							<input
								type="text"
								placeholder={t("helpCenter.placeholderSearchArticles")}
								value={searchQuery}
								onChange={(e) => {
									setSearchQuery(e.target.value)
								}}
								className="w-full bg-transparent text-md text-base-400 outline-none placeholder:text-base-400"
							/>
							{searchQuery && (
								<button
									onClick={handleClearSearch}
									className="shrink-0 text-base-400 hover:text-base-200"
								>
									<Icon name="close" size={20} />
								</button>
							)}
						</div>

						<ul>
							{helpItems.length > 0 ? (
								helpItems.map((item) => (
									<HelpItemComponent key={item.id} item={item} />
								))
							) : (
								<li className="flex px-5 pt-6">
									{isLoading ? (
										<div className="flex w-full justify-center">
											<Spinner size={24} />
										</div>
									) : (
										<span className="text-md text-base-400">
											{error ||
												(hasSearchQuery
													? t("helpCenter.noResultsFor", {
															query: searchQuery,
														})
													: t("helpCenter.noResults"))}
										</span>
									)}
								</li>
							)}
						</ul>

						<div className="px-5 py-4">
							{!isLoading && (
								<a
									href={helpCenterUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="block py-2 text-md font-semibold text-base-400 underline hover:text-base-100"
								>
									{linkText}
								</a>
							)}
						</div>

						<button
							onClick={handleContactClick}
							className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-b-md bg-base-700 px-5 py-4"
						>
							<Icon name="contact_us" size={20} className="text-base-400" />
							<span className="text-md font-semibold text-white">
								{t("helpCenter.contactUs")}
							</span>
						</button>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}
